import { Injectable, inject } from '@angular/core';
import { environment } from '../../environments/environment';
import { ErrorHandlingService } from '../error-handling/error-handling.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ValidationEntityType } from '../../models/netsuite/validation-entity-type.model';
import { Observable, of } from 'rxjs';
import { SpinnerService } from '../spinner';
import { HttpHeaderConstants } from '../http-header.constant';
import { catchError, finalize, retry } from 'rxjs/operators';
import { NetsuiteConnection } from '../../models/appcloud/netsuite/netsuite-connection.model';
import { NetsuiteEntity } from '../../models/appcloud/netsuite/netsuite-entity.model';

@Injectable({
    providedIn: 'root',
})
export class NetsuiteValidationService {
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);
    readonly #errorHandlingService = inject(ErrorHandlingService);

    private VALIDATION_URL: string = environment.publicApiHost + '/netsuite/validation';

    validateType(
        type: ValidationEntityType,
        connection: NetsuiteConnection,
        additionalHeaders: any = {}
    ): Observable<NetsuiteEntity> {
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams(),
        };

        return this.#http
            .post<NetsuiteEntity>(this.VALIDATION_URL + '/validate/' + type, connection, customHttpOptions)
            .pipe(
                retry(0),
                catchError(() => {
                    return of(null);
                })
            );
    }

    getServerTime(connection: NetsuiteConnection, additionalHeaders: any = {}): Observable<Date> {
        this.#spinnerService.addProcess(
            'NetsuiteValidationService.getServerTime',
            'Validating NetSuite connection details...'
        );

        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams(),
        };

        return this.#http.post<Date>(this.VALIDATION_URL + '/servertime', connection, customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to validate your connection. Please try again later.'
                );
                return of<Date>();
            }),
            finalize(() => this.#spinnerService.completeProcess('NetsuiteValidationService.getServerTime'))
        );
    }

    getRoles(connection: NetsuiteConnection, additionalHeaders: any = {}): Observable<any> {
        this.#spinnerService.addProcess('NetsuiteValidationService.getRoles', 'Fetching roles');

        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams(),
        };

        return this.#http.post(this.VALIDATION_URL + '/roles', connection, customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to retrieve Netsuite roles. Please try again later.'
                );
                return of<Date>();
            }),
            finalize(() => this.#spinnerService.completeProcess('NetsuiteValidationService.getRoles'))
        );
    }
}
